import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';

import DOMPurify from 'dompurify';
import {
  FiSend,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiEdit2,
  FiTrash2,
} from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.min.css';


const API_BASE_URL = process.env.REACT_APP_API_URL;

function MessageList() {
  const [messages, setMessages] = useState([]);
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const userPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const canRead = userPermissions.includes('read_Message');
  const canCreate = userPermissions.includes('create_Message');
  const canEdit = userPermissions.includes('update_Message');
  const canDelete = userPermissions.includes('delete_Message');

  const fetchMessages = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data.data || []);
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.status === 401
        ? 'Unauthorized access. Please log in again.'
        : 'Failed to fetch messages.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [token, canRead]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((prev) => prev.filter((msg) => msg._id !== id));
    } catch (err) {
      console.error(err);
      alert('Failed to delete the message.');
    }
  };

  const handleEdit = (msg) => {
    navigate('/AddMessage', { state: { messageToEdit: msg } });
  };

  const getTextContent = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const handleSearch = debounce((value) => {
    setSearch(value);
  }, 300);

  const filteredMessages = messages.filter((msg) => {
    const query = search.toLowerCase();
    const products = msg.productIds?.map((p) => p?.name || '').join(' ').toLowerCase();
    const buyers = msg.buyerIds?.map((b) => b?.email || '').join(' ').toLowerCase();
    const created = new Date(msg.createdAt).toLocaleDateString().toLowerCase();
    return products.includes(query) || buyers.includes(query) || created.includes(query);
  });

  if (!canRead) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          You do not have permission to view messages.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="fw-bold fs-4 text-dark mb-0">Messages</h2>
        {canCreate && (
          <button
            onClick={() => navigate('/AddMessage')}
            className="btn d-flex align-items-center gap-2"
            style={{ backgroundColor: '#102234', color: 'white' }}
          >
            <FiSend className="fs-5" />
            Send Message
          </button>
        )}
      </div>

      <div className="input-group mb-3">
        <span className="input-group-text bg-white border-end-0">
          <FiSearch />
        </span>
        <input
          className="form-control border-start-0"
          placeholder="Search by product, buyer, or date..."
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : filteredMessages.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">No messages found.</p>
          {canCreate && (
            <button
              onClick={() => navigate('/AddMessage')}
              className="btn btn-primary"
            >
              Create Your First Message
            </button>
          )}
        </div>
      ) : (
        filteredMessages.map((msg, index) => {
          const isExpanded = expandedMessageId === msg._id;
          const previewText = getTextContent(msg.message).split(' ').slice(0, 10).join(' ') + '...';

          return (
            <div
              key={msg._id}
              className="border-bottom py-3 px-2"
              role="button"
              aria-expanded={isExpanded}
              aria-controls={`message-${msg._id}`}
              style={{
                cursor: 'pointer',
                backgroundColor: isExpanded ? '#f0f4f8' : 'white',
              }}
              onClick={() => setExpandedMessageId(isExpanded ? null : msg._id)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <span
                    className="fw-bold text-secondary"
                    style={{
                      minWidth: '20px',
                      display: 'inline-block',
                      textAlign: 'right',
                    }}
                  >
                    {index + 1}.
                  </span>
                  <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                    <strong>{msg.sellerId?.email || 'Unknown Sender'}</strong>
                    <span className="text-muted small">{previewText}</span>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">{formatDate(msg.createdAt)}</span>
                  {canEdit && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(msg);
                      }}
                      aria-label="Edit message"
                    >
                      <FiEdit2 />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(msg._id);
                      }}
                      aria-label="Delete message"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                  {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                </div>
              </div>

              {isExpanded && (
                <div className="mt-3 ps-4" id={`message-${msg._id}`}>
                  <p><strong>Buyers:</strong></p>
                  <ul>
                    {msg.buyerIds?.map((b, i) => (
                      <li key={i}>{b.email}</li>
                    ))}
                  </ul>
                  <p><strong>Products:</strong> {msg.productIds?.map((p) => p?.name || '').join(', ')}</p>
                  <div
                    className="border p-3 bg-white"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.message) }}
                  />
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

export default MessageList;